<template>
  <div
    class="edit_sales_person-container edit_sales_person-main edit-sales-person-container glass-tab-page edit_sales_person-page--glass"
  >
    <BrucdcumHeader :breadcrumb="['Company', 'Sales Persons', 'Edit Sales Person']" />

    <form class="edit_sales_person-form" @submit.prevent="handleSaveAndClose">
      <div class="glass-tab-view glass-tab-view--flush edit_sales_person-glass-shell">
        <div class="glass-tab-view__content edit_sales_person-page-stack">
          <div class="edit_sales_person-header-block">
            <h2 class="edit_sales_person-title">Edit Sales Person</h2>
            <p class="edit_sales_person-subtitle">
              Update contact details, expense account, and employment information
            </p>
          </div>

          <div
            class="edit_sales_person-glass-panel glass-tab-panel glass-tab-panel--flex-column glass-tab-panel--stagger-rows"
            :class="{ 'edit_sales_person-glass-panel--loading': isPageLoading }"
          >
            <!-- Row 1: Name, Email, Phone -->
            <div class="glass-tab-panel__row edit_sales_person-form-group-row edit_sales_person-form-group-row--cols-3">
              <div class="edit_sales_person-form-group edit_sales_person-form-group--input">
                <LabelInputField
                  input-id="edit-sales-person-name"
                  label="Sales Person Name"
                  v-model="form.salesPersonName"
                  placeholder="Enter sales person name"
                  required
                  flex
                  :disabled="isPageLoading"
                  :has-error="showErrors && !form.salesPersonName.trim()"
                >
                  <div v-if="showErrors && !form.salesPersonName.trim()" class="edit_sales_person-error-text">
                    Sales person name is required
                  </div>
                </LabelInputField>
              </div>
              <div class="edit_sales_person-form-group edit_sales_person-form-group--input">
                <LabelInputField
                  input-id="edit-sales-person-email"
                  label="Email"
                  v-model="form.email"
                  type="email"
                  autocomplete="email"
                  placeholder="salesperson@company.com"
                  required
                  flex
                  :disabled="isPageLoading"
                  :has-error="showErrors && !form.email.trim()"
                >
                  <div v-if="showErrors && !form.email.trim()" class="edit_sales_person-error-text">
                    Email is required
                  </div>
                </LabelInputField>
              </div>
              <div class="edit_sales_person-form-group edit_sales_person-form-group--input">
                <LabelInputField
                  input-id="edit-sales-person-phone"
                  label="Phone"
                  v-model="form.phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  required
                  flex
                  :disabled="isPageLoading"
                  :has-error="showErrors && !form.phone.trim()"
                >
                  <div v-if="showErrors && !form.phone.trim()" class="edit_sales_person-error-text">
                    Phone is required
                  </div>
                </LabelInputField>
              </div>
            </div>

            <!-- Row 2: File As, Job Title, Website -->
            <div class="glass-tab-panel__row edit_sales_person-form-group-row edit_sales_person-form-group-row--cols-3">
              <div class="edit_sales_person-form-group edit_sales_person-form-group--input">
                <LabelInputField
                  input-id="edit-sales-person-file-as"
                  label="File as"
                  v-model="form.fileAs"
                  placeholder="How should this be filed?"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
              <div class="edit_sales_person-form-group edit_sales_person-form-group--input">
                <LabelInputField
                  input-id="edit-sales-person-job-title"
                  label="Job Title"
                  v-model="form.jobTitle"
                  placeholder="e.g., Sales Manager, Account Executive"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
              <div class="edit_sales_person-form-group edit_sales_person-form-group--input">
                <LabelInputField
                  input-id="edit-sales-person-website"
                  label="Website"
                  v-model="form.webpage"
                  type="url"
                  placeholder="https://www.example.com"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
            </div>

            <!-- Row 3: Fax, Expense Account -->
            <div class="glass-tab-panel__row edit_sales_person-form-group-row edit_sales_person-form-group-row--cols-2">
              <div class="edit_sales_person-form-group edit_sales_person-form-group--input">
                <LabelInputField
                  input-id="edit-sales-person-fax"
                  label="Fax"
                  v-model="form.fax"
                  type="tel"
                  placeholder="+1 (555) 123-4568"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
              <div class="edit_sales_person-form-group edit_sales_person-form-group--dropdown">
                <LabelDropdown
                  label="Expense Account"
                  select-id="edit-sales-person-expense-account"
                  v-model="form.expenseAccountId"
                  flex
                  wide
                  searchable
                  search-placeholder="Search accounts..."
                  :disabled="isPageLoading"
                  :options="expenseAccountDropdownOptions"
                  @change="handleExpenseAccountChange"
                />
              </div>
            </div>

            <!-- Row 4: Gender, Active -->
            <div class="glass-tab-panel__row edit_sales_person-form-group-row edit_sales_person-form-group-row--cols-2">
              <div class="edit_sales_person-form-group edit_sales_person-form-group--dropdown">
                <LabelDropdown
                  label="Gender"
                  select-id="edit-sales-person-gender"
                  v-model="form.gender"
                  flex
                  wide
                  :searchable="false"
                  :disabled="isPageLoading"
                  :options="genderDropdownOptions"
                />
              </div>
              <div class="edit_sales_person-form-group edit_sales_person-form-group--checkbox">
                <LabelCheckboxField
                  input-id="edit-sales-person-active"
                  label="Active"
                  v-model="form.active"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
            </div>

            <!-- Row 5: DOB, Date of Hire -->
            <div class="glass-tab-panel__row edit_sales_person-form-group-row edit_sales_person-form-group-row--cols-2">
              <div class="edit_sales_person-form-group edit_sales_person-form-group--date">
                <LabelDateField
                  input-id="edit-sales-person-dob"
                  label="Date of Birth"
                  v-model="form.dob"
                  placeholder="MM/DD/YYYY"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
              <div class="edit_sales_person-form-group edit_sales_person-form-group--date">
                <LabelDateField
                  input-id="edit-sales-person-hire"
                  label="Date of Hire"
                  v-model="form.dateOfHire"
                  placeholder="MM/DD/YYYY"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
            </div>

            <!-- Row 6: Last Review, Release -->
            <div class="glass-tab-panel__row edit_sales_person-form-group-row edit_sales_person-form-group-row--cols-2">
              <div class="edit_sales_person-form-group edit_sales_person-form-group--date">
                <LabelDateField
                  input-id="edit-sales-person-last-review"
                  label="Date of Last Review"
                  v-model="form.dateOfLastReview"
                  placeholder="MM/DD/YYYY"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
              <div class="edit_sales_person-form-group edit_sales_person-form-group--date">
                <LabelDateField
                  input-id="edit-sales-person-release"
                  label="Date of Release"
                  v-model="form.dateOfRelease"
                  placeholder="MM/DD/YYYY"
                  flex
                  :disabled="isPageLoading"
                />
              </div>
            </div>

            <!-- Row 7: Address -->
            <div class="glass-tab-panel__row edit_sales_person-form-group-row edit_sales_person-form-group-row--full">
              <div
                class="edit_sales_person-form-group edit_sales_person-form-group--description edit_sales_person-address-field"
                role="button"
                tabindex="0"
                @click="handleAddressClick"
                @keydown.enter="handleAddressClick"
                @keydown.space.prevent="handleAddressClick"
              >
                <LabelDescriptionField
                  input-id="edit-sales-person-address"
                  label="Address"
                  v-model="form.address"
                  :rows="3"
                  read-only
                  flex
                  placeholder="Click to enter address details"
                />
              </div>
            </div>
          </div>

          <div class="edit_sales_person-form-actions glass-tab-view__actions">
            <SecondaryButton label="Cancel" :disabled="isSaving" @click="handleCancel" />
            <PrimaryButton
              label="Update and Close"
              icon="bi bi-check-lg"
              :loading="isSaving"
              :disabled="isPageLoading"
              @click="handleSaveAndClose"
            />
          </div>
        </div>
      </div>
    </form>

    <AddressModal v-if="showAddressModal" :value="form.address" @ok="handleAddressOk" @cancel="handleAddressCancel" />
    <NewAccountCreationDialog
      :visible="showAccountModal"
      :companyId="companyId"
      @ok="handleAccountOk"
      @cancel="handleAccountCancel"
    />
    <ErrorModal
      :show="showErrorModal"
      title="Error"
      :message="errorMessage"
      @close="closeErrorModal"
    />
    <SuccessModal
      :show="showSuccessModal"
      title="Success"
      :message="successMessage"
      @close="closeSuccessModal"
    />
  </div>
</template>

<script>
import '../styles/EditSalesPerson.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import logger from '@/utils/logger';
import AddressModal from './AddressModel.vue';
import NewAccountCreationDialog from './NewAccountCreationDialog.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import LabelInputField from '@/modules/consultant/components/LabelInputField.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import LabelDateField from '@/modules/consultant/components/LabelDateField.vue';
import LabelCheckboxField from '@/modules/consultant/components/LabelCheckboxField.vue';
import LabelDescriptionField from '@/modules/consultant/components/LabelDescriptionField.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import ApiService from '@/services/api.service';

export default {
  name: 'EditSalesPerson',
  components: {
    BrucdcumHeader,
    AddressModal,
    NewAccountCreationDialog,
    ErrorModal,
    SuccessModal,
    LabelInputField,
    LabelDropdown,
    LabelDateField,
    LabelCheckboxField,
    LabelDescriptionField,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      form: {
        salesPersonName: '',
        fileAs: '',
        jobTitle: '',
        phone: '',
        fax: '',
        email: '',
        webpage: '',
        active: true,
        gender: 'Unspecified',
        dob: '',
        dateOfHire: '',
        dateOfLastReview: '',
        dateOfRelease: '',
        address: '',
        expenseAccountId: '',
      },
      expenseAccounts: [],
      showAddressModal: false,
      showAccountModal: false,
      isPageLoading: false,
      isSaving: false,
      showErrors: false,
      showErrorModal: false,
      errorMessage: '',
      showSuccessModal: false,
      successMessage: '',
      salesPersonId: null,
      companyId: null,
      navigateAfterSuccess: false,
    };
  },
  computed: {
    genderDropdownOptions() {
      return [
        { value: 'Unspecified', label: 'Unspecified' },
        { value: 'Male', label: 'Male' },
        { value: 'Female', label: 'Female' },
        { value: 'Other', label: 'Other' },
      ];
    },
    expenseAccountDropdownOptions() {
      const accounts = this.expenseAccounts.map((account) => ({
        value: account.id,
        label: `${account.accountName} (${account.accountNumber}) - ${account.accountType}`,
      }));
      return [
        { value: '', label: 'Select an expense account', disabled: true },
        ...accounts,
        { value: '__create_new__', label: 'Add New Account' },
      ];
    },
  },
  async mounted() {
    this.salesPersonId = this.$route.params.salesPersonId;
    this.companyId = this.$route.params.id;
    await Promise.all([this.loadSalesPerson(), this.fetchExpenseAccounts()]);
  },
  methods: {
    async loadSalesPerson() {
      this.isPageLoading = true;
      try {
        const response = await ApiService.get(`/company/sales-persons/${this.salesPersonId}`);
        const person = response.data;

        this.form = {
          salesPersonName: person.salesPersonName || '',
          fileAs: person.fileAs || '',
          jobTitle: person.jobTitle || '',
          phone: person.phone || '',
          fax: person.fax || '',
          email: person.email || '',
          webpage: person.webpage || '',
          active: person.active !== undefined ? person.active : true,
          gender: person.gender || 'Unspecified',
          dob: this.convertDateFromISO(person.dob),
          dateOfHire: this.convertDateFromISO(person.dateOfHire),
          dateOfLastReview: this.convertDateFromISO(person.dateOfLastReview),
          dateOfRelease: this.convertDateFromISO(person.dateOfRelease),
          address: this.formatAddress(person.addresses),
          expenseAccountId: person.expenseAccountId || '',
        };
      } catch (error) {
        logger.error('Failed to load sales person:', error);
        this.errorMessage = 'Failed to load sales person data';
        this.showErrorModal = true;
      } finally {
        this.isPageLoading = false;
      }
    },

    formatAddress(addresses) {
      if (!addresses || !addresses.length || !addresses[0]) return '';

      const address = addresses[0];
      const lines = [
        address.address1 || '',
        address.address2 || '',
        address.city || '',
        address.state || '',
        address.postalCode || '',
        address.country || '',
      ];

      return lines.join('\n');
    },

    async fetchExpenseAccounts() {
      try {
        const response = await ApiService.get(`/company-account/company/${this.companyId}`);
        this.expenseAccounts = (response.data.accounts || []).filter(
          (acc) => acc.accountType === 'Expense' || acc.accountType === 'Other Expense'
        );
      } catch (error) {
        logger.error('Failed to load expense accounts:', error);
      }
    },

    handleExpenseAccountChange(value) {
      if (value === '__create_new__') {
        this.showAccountModal = true;
        this.$nextTick(() => {
          this.form.expenseAccountId = '';
        });
      }
    },

    convertDateFromISO(isoDate) {
      if (!isoDate) return '';

      const date = new Date(isoDate);
      if (Number.isNaN(date.getTime())) return '';

      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();

      return `${month}/${day}/${year}`;
    },

    convertDateToISO(dateString) {
      if (!dateString) return null;

      const parts = dateString.split('/');
      if (parts.length !== 3) return null;

      const month = parts[0];
      const day = parts[1];
      const year = parts[2];

      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    },

    validateForm() {
      this.showErrors = true;
      return (
        Boolean(this.form.salesPersonName.trim()) &&
        Boolean(this.form.email.trim()) &&
        Boolean(this.form.phone.trim())
      );
    },

    async handleSaveAndClose() {
      if (!this.validateForm()) {
        this.errorMessage = 'Please fill in all required fields';
        this.showErrorModal = true;
        return;
      }

      this.isSaving = true;
      try {
        await this.saveSalesPerson();
        this.successMessage = 'Sales person updated successfully!';
        this.showSuccessModal = true;
        this.navigateAfterSuccess = true;
      } catch (error) {
        logger.error('Failed to update sales person:', error);
        this.errorMessage =
          error.response?.data?.message || error.message || 'Failed to update sales person';
        this.showErrorModal = true;
      } finally {
        this.isSaving = false;
      }
    },

    async saveSalesPerson() {
      let addressObj = null;
      if (this.form.address && this.form.address.trim()) {
        const lines = this.form.address.split('\n');
        const paddedLines = [...lines, '', '', '', '', '', ''].slice(0, 6);
        addressObj = {
          address1: paddedLines[0] || '',
          address2: paddedLines[1] || '',
          city: paddedLines[2] || '',
          state: paddedLines[3] || '',
          postalCode: paddedLines[4] || '',
          country: paddedLines[5] || '',
        };
      }

      const payload = {
        salesPersonName: this.form.salesPersonName.trim(),
        fileAs: this.form.fileAs.trim(),
        jobTitle: this.form.jobTitle.trim(),
        phone: this.form.phone.trim(),
        fax: this.form.fax.trim(),
        expenseAccountId: this.form.expenseAccountId || null,
        email: this.form.email.trim(),
        webpage: this.form.webpage.trim(),
        active: this.form.active,
        gender: this.form.gender,
        dob: this.convertDateToISO(this.form.dob),
        dateOfHire: this.convertDateToISO(this.form.dateOfHire),
        dateOfLastReview: this.convertDateToISO(this.form.dateOfLastReview),
        dateOfRelease: this.convertDateToISO(this.form.dateOfRelease),
        address: addressObj,
      };

      const response = await ApiService.put(`/company/sales-persons/${this.salesPersonId}`, payload);
      return response.data;
    },

    navigateBack() {
      this.$router.push(`/company/${this.companyId}/dashboard/company/lists/salespersons`);
    },

    handleCancel() {
      this.navigateBack();
    },

    handleAddressOk(addressData) {
      this.form.address = addressData || '';
      this.showAddressModal = false;
    },

    handleAddressCancel() {
      this.showAddressModal = false;
    },

    handleAddressClick() {
      this.showAddressModal = true;
    },

    async handleAccountOk(createdAccount) {
      this.showAccountModal = false;
      if (createdAccount) {
        await this.fetchExpenseAccounts();
        this.form.expenseAccountId = createdAccount.id;
        if (this.$toast) {
          this.$toast.success('Account created successfully!');
        }
      }
    },

    handleAccountCancel() {
      this.showAccountModal = false;
    },

    closeErrorModal() {
      this.showErrorModal = false;
      this.errorMessage = '';
    },

    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successMessage = '';
      if (this.navigateAfterSuccess) {
        this.navigateBack();
      }
    },
  },
};
</script>
